import { Eye, EyeOff, Shield, User } from 'lucide-react';
import React, { useState } from 'react';
import { AccountDeletionModal } from '../components/profile/AccountDeletionModal';
import { BasicProfileForm } from '../components/profile/BasicProfileForm';
import { ProfilePictureUploader } from '../components/profile/ProfilePictureUploader';
import { TwoFactorAuthSetup } from '../components/profile/TwoFactorAuthSetup';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useUser } from '../contextapi/UserContext';
import { useToast } from '../hooks/use-toast';

export const EnhancedProfileSettings: React.FC = () => {
  const { user } = useUser();
  const toastHook = useToast();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const handleProfileSave = (profileData: any) => {
    console.log('Profile saved:', profileData);
  };

  const handleAvatarUpload = (avatarUrl: string) => {
    console.log('Avatar uploaded:', avatarUrl);
  };

  const handlePasswordChange = async () => {
    // Clear any previous errors
    setPasswordError(null);

    // Validate password length
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long.');
      return;
    }

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match. Please try again.');
      return;
    }

    setPasswordLoading(true);
    try {
      const { supabase } = await import('../utils/supabaseClient');

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        throw error;
      }

      toastHook.toast({
        title: 'Password Updated',
        description: 'Your password has been successfully changed.',
      });

      // Reset form and close dialog
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordDialog(false);
    } catch (error) {
      console.error('Error updating password:', error);
      // Show API errors in the dialog
      setPasswordError(error instanceof Error ? error.message : 'Failed to update password. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordInputChange = (value: string) => {
    setNewPassword(value);
    // Clear error when user starts typing
    if (passwordError) setPasswordError(null);
  };

  const handleConfirmPasswordInputChange = (value: string) => {
    setConfirmPassword(value);
    // Clear error when user starts typing
    if (passwordError) setPasswordError(null);
  };

  const handleClosePasswordDialog = () => {
    setShowPasswordDialog(false);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Please log in</h2>
          <p className="text-gray-600 mt-2">You need to be logged in to access your profile settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
          <p className="text-gray-600 mt-2">
            Manage your personal information and account preferences
          </p>
        </div>


        {/* Main Content - No Tabs, Single View */}
        <div className="space-y-8">
          {/* Profile Section */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <User className="h-5 w-5" />
              <h2 className="text-2xl font-bold">Profile</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Profile Picture */}
              <div className="lg:col-span-1">
                <ProfilePictureUploader
                  onUploadComplete={handleAvatarUpload}
                  showTitle={true}
                />
              </div>

              {/* Basic Profile Form */}
              <div className="lg:col-span-2">
                <BasicProfileForm
                  onSave={handleProfileSave}
                  showSaveButton={true}
                />
              </div>
            </div>
          </div>

          {/* Security Section */}
          <div className="space-y-6 pt-6 border-t">
            {/* <div className="flex items-center gap-2 mb-4">
              <Shield className="h-5 w-5" />
              <h2 className="text-2xl font-bold">Security</h2>
            </div> */}

            {/* <TwoFactorAuthSetup onSetupComplete={() => {
              console.log('2FA setup completed');
            }} /> */}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Security Settings
                </CardTitle>
                <CardDescription>
                  Manage other security settings for your account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">Password</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Change your account password
                    </p>
                    <Button
                      onClick={() => setShowPasswordDialog(true)}
                      className="bg-gray-600 text-white hover:bg-gray-700"
                    >
                      Change Password
                    </Button>
                  </div>

                  <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
                    <h4 className="font-medium mb-2 text-red-900">Danger Zone</h4>
                    <p className="text-sm text-red-700 mb-3">
                      Permanently delete your account and all associated datasferg
                    </p>
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700"
                    >
                      Delete Account
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Account Deletion Modal */}
        <AccountDeletionModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onDeletionComplete={() => {
            // Handle deletion complete - user will be signed out
            setShowDeleteModal(false);
          }}
        />

        {/* Change Password Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={handleClosePasswordDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
              <DialogDescription>
                Enter your new password below.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => handlePasswordInputChange(e.target.value)}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => handleConfirmPasswordInputChange(e.target.value)}
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>

              {/* Error message display in dialog */}
              {passwordError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">{passwordError}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleClosePasswordDialog}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePasswordChange}
                disabled={passwordLoading || !newPassword || !confirmPassword}
                className="bg-primary text-primary-foreground"
              >
                {passwordLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default EnhancedProfileSettings;
